import React, { useEffect, useState } from 'react';
import { THEMES } from '../shared/theme/themes';
import { useTheme } from '../shared/theme/useTheme';
import { useAnimations } from '../shared/animations/useAnimations';
import './ThemePanel.css';

/**
 * Slide-in panel for themes + animation toggle.
 * Toggle with / key or Escape to close.
 */
export default function ThemePanel() {
  const [isOpen, setIsOpen] = useState(false);
  const { themeId, setThemeId } = useTheme();
  const { animationsEnabled, toggleAnimations } = useAnimations();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      if (isTyping) return;

      if (e.key === '/') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      {/* Click-outside overlay */}
      {isOpen && (
        <div
          className="theme-overlay"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <aside
        className={`theme-panel${isOpen ? ' theme-panel--open' : ''}`}
        aria-label="Appearance settings"
        aria-hidden={!isOpen}
      >
        <div className="theme-panel__header">
          <span className="theme-panel__title">Appearance</span>
          <button
            className="theme-panel__close"
            onClick={() => setIsOpen(false)}
            aria-label="Close appearance panel"
          >
            ✕
          </button>
        </div>

        {/* Animations toggle */}
        <div className="theme-panel__section">
          <button
            className={`anim-toggle${animationsEnabled ? ' anim-toggle--on' : ''}`}
            onClick={toggleAnimations}
            aria-pressed={animationsEnabled}
          >
            <span className="anim-toggle__label">
              {animationsEnabled ? '✦' : '○'} Animations
            </span>
            <span className="anim-toggle__state">
              {animationsEnabled ? 'On' : 'Off'}
            </span>
          </button>
        </div>

        <div className="theme-panel__divider" />

        {/* Theme list */}
        <ul className="theme-panel__list" role="radiogroup" aria-label="Visual themes">
          {THEMES.map((theme) => {
            const isActive = themeId === theme.id;
            return (
              <li key={theme.id}>
                <button
                  className={`theme-swatch${isActive ? ' theme-swatch--active' : ''}`}
                  onClick={() => setThemeId(theme.id)}
                  role="radio"
                  aria-checked={isActive}
                >
                  <div
                    className="theme-swatch__bar"
                    style={{ background: theme.previewGradient }}
                    aria-hidden="true"
                  />
                  <div className="theme-swatch__info">
                    <span className="theme-swatch__name">
                      {theme.emoji} {theme.name}
                    </span>
                    <span className="theme-swatch__desc">{theme.description}</span>
                  </div>
                  {isActive && (
                    <span className="theme-swatch__check" aria-hidden="true">✓</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
    </>
  );
}
